import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const CANADA_POST_KEY = window.__ENV__.CANADA_POST_KEY;
const CANADA_POST_SUGGESTION_URL = window.__ENV__.CANADA_POST_ADDRESS_SUGGESTION_API_URL;

// Action names
const ACTION_NAME_PREFIX = 'action/getCanadaPostAddressSuggestion';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Find addresses matching the search term and country. Returns the array of object which has address, description and
 * id to display in address dropdown. Document link for Canada Post
 * API - https://www.canadapost-postescanada.ca/ac/support/api/addresscomplete-interactive-find/
 * @param {string} search term to get matching address of it.
 * @param {string} country to get matching address of that country only.
 * @param {string} id address id from Canada Post API dropdown list, used here to get all addresses under that address.
 * @param {object} logger
 * @
 */
export const GetCanadaPostAddressSuggestion = (search, country, id) => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { language } = getState().Authentication;

        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Fetching list of address from Canada Post API.`, {
            correlation: { transactionId },
        });

        // step 2: await Web service call
        const canadaPostSuggestion = await fetch(
            `${CANADA_POST_SUGGESTION_URL}?key=${CANADA_POST_KEY}&LastId=${id}&provider=AddressComplete&package=Interactive&service=Find&version=2.1&endpoint=json.ws&LanguagePreference=${language}&Country=${country}&SearchTerm=${search}`,
            {
                method: 'GET',
            }
        );

        const canadaPostSuggestionResponse = await canadaPostSuggestion.json();

        if (canadaPostSuggestionResponse && canadaPostSuggestionResponse[0] && canadaPostSuggestionResponse[0].Error) {
            const { Error: error, Cause } = canadaPostSuggestionResponse[0];
            logger.error(
                `${ACTION_NAME_PREFIX}: Failure when fetching list of address from Canada Post API. Code: ${error} Reason: ${Cause}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code: error,
                message: Cause,
            });
        }
        // step 3: Response Action
        return dispatch({
            type: RESPONSE_ACTION_NAME,
            suggestionDropDownAddressList: canadaPostSuggestionResponse.Items,
            success: true,
        });
    };
};

/**
 * Updating isLoadingSuggestionDropDownAddressList to true when Canada Post Api is being called.
 * @param {object} state
 */
export const requestReducer = produce((state) => {
    state.CanadaPostAddress.isLoadingSuggestionDropDownAddressList = true;
});

/**
 * Fired off when the request receives the response.
 * @param {object} state
 * @param {object} action
 */
export const responseReducer = produce((state, action) => {
    state.CanadaPostAddress.isLoadingSuggestionDropDownAddressList = false;
    state.Errors.canadaPostAddressLoad.hasError = false;
    state.Errors.canadaPostAddressLoad.code = '';
    state.Errors.canadaPostAddressLoad.message = '';
    if (action.suggestionDropDownAddressList) {
        state.CanadaPostAddress.suggestionDropDownAddressList = action.suggestionDropDownAddressList;
    } else {
        state.CanadaPostAddress.suggestionDropDownAddressList = [];
    }
});

/**
 * Fired off when the request encouters an error.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.CanadaPostAddress.isLoadingSuggestionDropDownAddressList = false;
    state.Errors.canadaPostAddressLoad.hasError = true;
    state.Errors.canadaPostAddressLoad.code = action.code;
    state.Errors.canadaPostAddressLoad.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
